import React from 'react';
import { useSelector } from 'react-redux';
import { GroupFinalCalculation } from '~/Api/User/ProductGroup/CzReimbursement';
import { EmptyResultReason } from '~/CzReimbursement/EmptyResultReason';
import { selectGroup } from '~/CzReimbursement/reducer';
import { State } from '~/Store';

import styles from './styles.less';
import { Typography } from '~/libs/ui/atoms/Typography';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';
import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';
import Icon from '~/UI/Icon';

const NewReimbursementPerUTDD: React.FunctionComponent<
	React.PropsWithChildren<{
		finalCalculation: GroupFinalCalculation;
	}>
> = ({ finalCalculation }) => {
	if (finalCalculation.baseReimbursement === null) {
		return null;
	}

	switch (finalCalculation.baseReimbursement.finalPrice) {
		case 'lowest-ex-factory':
			return <>{finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()}</>;
		case 'average-of-2nd-and-3rd-lowest-ex-factory':
			return (
				<>{finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()}</>
			);
		default:
			return null;
	}
};

export const GroupCalculation: React.FunctionComponent<React.PropsWithChildren<{ groupId: string }>> = ({
	groupId,
}) => {
	const czReimbursement = useSelector((state: State) => selectGroup(state.czReimbursement, groupId));
	if (czReimbursement === null || czReimbursement.group === null) {
		return null;
	}

	const { finalCalculation } = czReimbursement.group;

	return (
		<div className={styles.container}>
			{finalCalculation.emptyResultReason.length > 0 && (
				<div className='mb-4'>
					{finalCalculation.emptyResultReason.map((error) => (
						<p className='text-[14px] leading-snug font-semibold tracking-wide text-[#ef3f6a]'>
							<EmptyResultReason error={error} key={error.code} />
						</p>
					))}
				</div>
			)}

			<div className={styles.columns}>
				<div className={styles.leftColumn}>
					<Typography level={5} color='black'>
						Lowest ex-factory prices: <span className='block text-[12px] text-gray-500'>(reference product)</span>
					</Typography>
					<div className='divide-y divide-gray-200'>
						{finalCalculation.lowestPrices.map((lowestPrice, i) => (
							<div className='w-full flex flex-nowrap items-center justify-between py-2' key={lowestPrice.id}>
								<div className='w-0 grow shrink flex flex-nowrap items-center justify-start'>
									<span className='grow-0 shrink-0 w-6 text-gray-500 text-left'>{i + 1}.</span>
									<CountrySourceWithTooltip
										countryAlpha={lowestPrice.dataSource.country.alpha2}
										countryName={lowestPrice.dataSource.country.name}
										dataSourceShortName={lowestPrice.dataSource.shortName}
										dataSourceDescription={lowestPrice.dataSource.description}
									/>
								</div>
								<div className='block pl-2 grow-0 shrink-0 text-[14px] leading-tight tracking-wider'>
									{lowestPrice.exFactoryPrice.roundedHumanFormat()}
								</div>
							</div>
						))}
					</div>
				</div>
				<div className={styles.rightColumn}>
					<Typography level={5} color='black' className='mb-2'>
						Lowest price per ODTD:
					</Typography>

					<div className={styles.row}>
						<p className={styles.rowName}>Reference product:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement?.referenceProduct?.packName}
							{finalCalculation.baseReimbursement?.referenceProduct?.packName &&
								finalCalculation.baseReimbursement?.referenceProduct?.suklCode && <>,&nbsp;</>}
							{finalCalculation.baseReimbursement?.referenceProduct?.suklCode}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Lowest ex-factory price per ODTD:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null ? (
								finalCalculation.baseReimbursement.finalPrice === 'lowest-ex-factory' ? (
									<strong>{finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()}</strong>
								) : (
									finalCalculation.baseReimbursement.lowestExFactoryPerUTDD?.roundedHumanFormat()
								)
							) : null}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Average of 2nd and 3rd lowest ex-factory price per ODTD:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null ? (
								finalCalculation.baseReimbursement.finalPrice === 'average-of-2nd-and-3rd-lowest-ex-factory' ? (
									<strong>
										{finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()}
									</strong>
								) : (
									finalCalculation.baseReimbursement.averageOf2ndAnd3rdLowestExFactoryPricePerUTDD?.roundedHumanFormat()
								)
							) : null}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Deviation:</p>
						<p className={styles.rowValue}>
							{finalCalculation.baseReimbursement !== null &&
							finalCalculation.baseReimbursement.deviationPercentage !== null
								? `${finalCalculation.baseReimbursement.deviationPercentage}%`
								: null}
						</p>
					</div>

					<Typography level={5} color='black' className='mb-2'>
						Possible savings:
					</Typography>

					<div className={styles.row}>
						<p className={styles.rowName}>
							Spending per group per
							{finalCalculation.possibleSavings.spendingPerGroup !== null
								? ` ${finalCalculation.possibleSavings.spendingPerGroup.year}`
								: null}
							:
						</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.spendingPerGroup?.price.roundedHumanFormat()}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>EKV1 (Current reimbursement per ODTD):</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.currentReimbursementPerUTDD?.price.roundedHumanFormat()}

							{finalCalculation.possibleSavings.currentReimbursementPerUTDD?.sameValueInAllLists === false ? (
								<TailwindTooltip
									reference={<Icon name='yellow-question-mark'>!</Icon>}
									displayClass='relative mt-px inline-block h-4 ml-2 align-top'
									position='top'
								>
									There are products with different EKV1 in the group. The most frequent value is displayed.
								</TailwindTooltip>
							) : null}
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>New reimbursement per ODTD:</p>
						<p className={styles.rowValue}>
							<NewReimbursementPerUTDD finalCalculation={finalCalculation} />
						</p>
					</div>

					<div className={styles.row}>
						<p className={styles.rowName}>Possible savings:</p>
						<p className={styles.rowValue}>
							{finalCalculation.possibleSavings.possibleSavingsPercentage !== null
								? `${finalCalculation.possibleSavings.possibleSavingsPercentage}%`
								: null}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};
